/**
 * Tenant Detection Utility
 *
 * Detecta o tenant atual baseado no subdomínio da URL
 * Suporta desenvolvimento local com subdomínio.localhost
 */

/**
 * Extrai o subdomínio do tenant da URL atual
 *
 * Exemplos:
 * - rhoden.joshua.app → 'rhoden'
 * - zafiro.joshua.app → 'zafiro'
 * - rhoden.localhost:3000 → 'rhoden' (dev)
 * - localhost:3000 → null (dev sem tenant)
 * - joshua.app → null (domínio raiz)
 *
 * @returns {string | null} Subdomínio do tenant ou null se não detectado
 */
export const getTenantSubdomain = (): string | null => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;

  // Desenvolvimento: localhost puro sem subdomínio
  if (hostname === 'localhost') {
    // Tentar localStorage para desenvolvimento (pode definir manualmente)
    return localStorage.getItem('dev_tenant_subdomain') || null;
  }

  // Dividir hostname em partes
  const parts = hostname.split('.');

  // Desenvolvimento: tenant.localhost
  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0];
  }

  // Produção: tenant.joshua.app (3+ partes)
  if (parts.length >= 3) {
    // Primeiro segmento é o subdomínio
    return parts[0];
  }

  // Domínio raiz ou não reconhecido
  return null;
};

/**
 * Define manualmente o tenant para desenvolvimento local
 * Útil para testar diferentes tenants sem configurar /etc/hosts
 *
 * @param subdomain - Subdomínio do tenant ou null para limpar
 */
export const setDevTenant = (subdomain: string | null): void => {
  if (subdomain) {
    localStorage.setItem('dev_tenant_subdomain', subdomain);
  } else {
    localStorage.removeItem('dev_tenant_subdomain');
  }
  // Recarregar para aplicar mudanças
  window.location.reload();
};

/**
 * Verifica se está em modo de desenvolvimento
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV ||
         window.location.hostname === 'localhost' ||
         window.location.hostname.endsWith('.localhost');
};
